/**
 * apps/admin — Écran « Mise à jour » : pousse un paquet de mise à jour vers l'instance
 * SÉLECTIONNÉE (celle que la console administre : LAN en direct, ou site distant via le relais).
 *
 * Le paquet part en BASE64 JSON (api.raw → /api/update/upload-json), jamais en multipart : le
 * relais WebSocket central ré-encode le corps en JSON et le multipart n'y survivrait pas — même
 * contrainte que l'upload de logo. Le manifeste du paquet décide de la cible (front à chaud /
 * moteur redémarré) ; l'instance vérifie la SIGNATURE avant d'appliquer.
 *
 * La signature reste HORS navigateur : l'exploitant signe localement (deploy/update-keys.mjs)
 * et joint le fichier .sig. La clé privée ne transite jamais par la console.
 *
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
import React, { useCallback, useEffect, useState } from 'react';

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error || new Error('lecture du fichier impossible'));
    r.readAsDataURL(file);
  });
}

export default function PublishUpdate({ api }) {
  const [etat, setEtat] = useState(null);
  const [erreurEtat, setErreurEtat] = useState('');
  const [pkg, setPkg] = useState(null);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sortie, setSortie] = useState(null); // { ok, texte }

  const rafraichir = useCallback(async () => {
    try {
      const r = await api.raw.get('/update/status');
      setEtat(r.data?.data || null);
      setErreurEtat('');
    } catch (e) {
      setErreurEtat(e?.response?.data?.error || e.message || 'statut indisponible');
    }
  }, [api]);

  useEffect(() => { rafraichir(); }, [rafraichir]);

  async function publier() {
    if (!pkg) { setSortie({ ok: false, texte: "Choisissez d'abord un paquet .tar.gz." }); return; }
    setBusy(true); setSortie({ ok: true, texte: 'Envoi et application en cours…' });
    try {
      const dataUrl = await readAsDataUrl(pkg);
      const signature = sig ? (await sig.text()).trim() : undefined;
      const r = await api.raw.post('/update/upload-json', { dataUrl, filename: pkg.name, signature });
      const d = r.data?.data || {};
      const journal = (d.journal || []).join('\n');
      if (d.launched) {
        setSortie({ ok: true, texte: `⟳ Moteur : mise à jour lancée (v${d.version || '?'}). Le serveur va redémarrer.\n\n${journal}` });
      } else {
        setSortie({ ok: true, texte: `✔ Appliqué : ${(d.applied || []).join(', ')} · version ${d.version || '?'} · redémarrage : ${d.restarted ? 'OUI' : 'NON (à chaud)'}\n\n${journal}` });
      }
      setTimeout(rafraichir, 1500);
    } catch (e) {
      const data = e?.response?.data;
      const journal = (data?.data?.journal || []).join('\n');
      setSortie({ ok: false, texte: `✗ ${data?.error || e.message}${journal ? '\n\n' + journal : ''}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>🔄 Mise à jour de l'instance</h2>
      <p className="muted">
        Déposez une archive <code>.tar.gz</code> à manifeste. L'instance lit sa cible
        (front, moteur ou les deux) et l'applique — le front est remplacé à chaud, le moteur
        redémarre. Le paquet est vérifié par signature avant application.
      </p>

      {erreurEtat && <p className="error">Statut : {erreurEtat}</p>}
      {etat && (
        <ul className="muted" style={{ lineHeight: 1.8 }}>
          <li>Front servi : <code>{etat.front}</code></li>
          <li>Disposition : <code>{etat.layout}</code></li>
          <li>Signature : <strong>{etat.signatureRequise ? 'OBLIGATOIRE' : 'optionnelle'}</strong></li>
          {etat.engine && <li>Dernière MàJ moteur : <code>{etat.engine.version}</code> {etat.engine.ok ? '✔' : '✗'}</li>}
        </ul>
      )}

      <div style={{ margin: '1rem 0' }}>
        <label>Paquet (.tar.gz)</label><br />
        <input type="file" accept=".gz,.tgz" onChange={(e) => setPkg(e.target.files[0] || null)} />
      </div>
      <div style={{ margin: '1rem 0' }}>
        <label>Signature (.sig) {etat?.signatureRequise ? '— obligatoire' : '— optionnelle'}</label><br />
        <input type="file" accept=".sig,text/plain" onChange={(e) => setSig(e.target.files[0] || null)} />
      </div>

      <p className="muted" style={{ fontSize: '.85rem' }}>
        Signez le paquet localement avant de le pousser : <code>node deploy/update-keys.mjs sign
        &lt;paquet&gt; &lt;clé-privée&gt;</code>. La clé privée ne passe jamais par la console.
      </p>

      <button onClick={publier} disabled={busy || !pkg}>Publier vers l'instance</button>

      {sortie && (
        <pre className={sortie.ok ? 'status' : 'error'} style={{ whiteSpace: 'pre-wrap', marginTop: '1rem' }}>
          {sortie.texte}
        </pre>
      )}
    </div>
  );
}
